import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { api, exportCsv } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Check } from "@/components/Check";
import {
  Plus,
  Download,
  Upload,
  Trash2,
  Settings as Gear,
  X,
  GripVertical,
  ArrowUpDown,
} from "lucide-react";
import useResizableColumns from "@/lib/useResizableColumns";
import ImportModal from "@/components/ImportModal";
import LastImportBanner from "@/components/LastImportBanner";
import { useTableSort, sortRows, SortCaret, useManualOrder, applyManualOrder, useRowDrag, SortStatus, reorderIds } from "@/components/TableSort";

const dollars = (cents) => `$${((cents || 0) / 100).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function TabPage() {
  const { tabId } = useParams();
  const [tab, setTab] = useState(null);
  const [rows, setRows] = useState([]);
  const [configOpen, setConfigOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importBump, setImportBump] = useState(0);
  const { user } = useAuth();
  const tableKey = `tab-${tabId || "x"}:${user?.id || "anon"}`;
  const { sort, toggle } = useTableSort(tableKey);
  const { order, setOrder, reset: resetOrder } = useManualOrder(tableKey);
  const [manual, setManual] = useState(false);
  const onSort = (f, t) => { setManual(false); toggle(f, t); };
  const displayRef = useRef([]);
  const drag = useRowDrag(manual, (dragId, targetId) =>
    setOrder(reorderIds(displayRef.current.map((r) => r.id), dragId, targetId)));

  // Column resize per-tab (each tab gets its own persisted layout).
  const colCount = useMemo(() => {
    if (!tab) return 12;
    if (tab.type === "tracker") return 11 + (tab.services?.length || 0);
    return 6;
  }, [tab]);
  const { setRef, widthStyle, Grip, reset } = useResizableColumns(
    `tab-${tabId || "x"}`, colCount, { minWidth: 50 },
  );

  const load = useCallback(async () => {
    if (!tabId) return;
    const tabs = await api.get("/tabs").then((r) => r.data);
    const t = (Array.isArray(tabs) ? tabs : []).find((x) => x.id === tabId);
    setTab(t || null);
    if (t) {
      const rs = await api.get(`/tabs/${tabId}/rows`).then((r) => r.data);
      setRows(Array.isArray(rs) ? rs : []);
    }
  }, [tabId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!tab) {
    return (
      <div className="mono text-muted-foreground uppercase tracking-widest text-xs">
        Loading…
      </div>
    );
  }

  const isTracker = tab.type === "tracker";

  const rowAccessor = (r, f) => {
    if (f === "total") return isTracker ? r.total_cents : r.amount_cents;
    if (f === "amount") return r.amount_cents;
    if (f.startsWith("svc:")) return (r.services || {})[f.slice(4)] ? 1 : 0;
    return r[f];
  };
  const sortedRows = sortRows(rows, sort, rowAccessor);
  const manualActive = manual;
  const display = manualActive ? applyManualOrder(rows, order) : sortedRows;
  displayRef.current = display;
  const labelFor = (f) => {
    if (f === "subject") return tab.subject_label || "Subject";
    if (f === "total") return isTracker ? "Total" : "Amount";
    if (f === "amount") return "Amount";
    if (f === "list_done") return "Done";
    if (f.startsWith("svc:")) {
      const s = (tab.services || []).find((x) => x.id === f.slice(4));
      return s ? s.name : "Service";
    }
    return f.charAt(0).toUpperCase() + f.slice(1);
  };

  const add = async () => {
    const r = await api.post(`/tabs/${tab.id}/rows`, {}).then((r) => r.data);
    setRows((p) => [r, ...p]);
  };

  const update = async (rid, patch) => {
    const cur = rows.find((r) => r.id === rid);
    if (!cur) return;
    const next = { ...cur, ...patch };
    setRows((p) => p.map((r) => (r.id === rid ? next : r)));
    const fresh = await api.put(`/tab-rows/${rid}`, patch).then((r) => r.data);
    setRows((p) => p.map((r) => (r.id === rid ? fresh : r)));
  };

  const remove = async (rid) => {
    if (!window.confirm("Delete this row?")) return;
    setRows((p) => p.filter((r) => r.id !== rid));
    await api.delete(`/tab-rows/${rid}`);
  };

  const toggleService = (row, svcId, v) => {
    const services = { ...(row.services || {}), [svcId]: v };
    update(row.id, { services });
  };

  const onExport = () =>
    exportCsv(`${tab.label.toLowerCase().replace(/\s+/g, "-")}.csv`, rows);

  const totalRevenue = rows.reduce(
    (s, r) => s + (isTracker ? (r.total_cents || 0) : (r.amount_cents || 0)),
    0
  );
  const paidCount = rows.filter((r) => (isTracker ? r.paid : r.list_done)).length;
  const doneCount = rows.filter((r) => (isTracker ? r.done : r.list_done)).length;

  const statusChipCls = (s) =>
    `hud-chip ${s === "Completed" || s === "Paid / In Progress" ? "hud-chip-on" : ""}`;

  return (
    <div className="space-y-6" data-testid={`tab-${tab.id}`}>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kpi-label">{isTracker ? "Tracker" : "List"} · {tab.label}</div>
          <h1 className="display text-3xl sm:text-5xl mt-3 leading-none font-bold uppercase tracking-tight">
            {dollars(totalRevenue)}
          </h1>
          <div className="mono text-xs text-muted-foreground tracking-widest mt-2 uppercase">
            {rows.length} {isTracker ? "orders" : "items"} · {paidCount} paid · {doneCount} done
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setConfigOpen(true)}
            className="hud-btn flex items-center gap-2"
            data-testid="tab-configure"
          >
            <Gear size={11} /> CONFIGURE
          </button>
          <button onClick={add} className="hud-btn flex items-center gap-2" data-testid="tab-add-row">
            <Plus size={11} /> NEW {isTracker ? "ORDER" : "ITEM"}
          </button>
          <button onClick={onExport} className="hud-btn flex items-center gap-2" data-testid="tab-export">
            <Download size={11} /> CSV
          </button>
          <button onClick={() => setImportOpen(true)} className="hud-btn flex items-center gap-2" data-testid="tab-import">
            <Upload size={11} /> IMPORT
          </button>
          <button
            onClick={() => setManual((m) => !m)}
            className={`hud-btn flex items-center gap-2 ${manual ? "hud-btn-primary" : ""}`}
            title="Drag rows into a custom order"
            data-testid="tab-manual-order"
          >
            <ArrowUpDown size={11} /> MANUAL ORDER
          </button>
          <button onClick={reset} className="hud-btn !text-[10px]" title="Reset column widths" data-testid="tab-reset-widths">
            RESET WIDTHS
          </button>
        </div>
      </header>

      <div className="flex items-center justify-between gap-3 -mt-2">
        <SortStatus
          sort={sort}
          manual={manualActive}
          labelFor={labelFor}
          shown={display.length}
          total={rows.length}
          onResetOrder={resetOrder}
        />
      </div>

      <LastImportBanner target="tab" tabId={tab.id} onChanged={load} refreshToken={importBump} />

      <div className="hud-frame overflow-hidden">
        <div className="overflow-x-auto">
          {isTracker ? (
            <table className="ox-table" style={{ tableLayout: "fixed", width: "max-content", minWidth: "100%" }}>
              <thead>
                <tr>
                  <th ref={setRef(0)} style={widthStyle(0, "160px")}>Client<SortCaret field="client" type="string" sort={sort} toggle={onSort} testId="sort-tab-client" /><Grip index={0} /></th>
                  <th ref={setRef(1)} style={widthStyle(1, "180px")}>{tab.subject_label || "Subject"}<SortCaret field="subject" type="string" sort={sort} toggle={onSort} /><Grip index={1} /></th>
                  <th ref={setRef(2)} style={widthStyle(2, "130px")}>Date<SortCaret field="date" type="date" sort={sort} toggle={onSort} /><Grip index={2} /></th>
                  <th ref={setRef(3)} style={widthStyle(3, "110px")}>Time<SortCaret field="time" type="string" sort={sort} toggle={onSort} /><Grip index={3} /></th>
                  {(tab.services || []).map((s, i) => (
                    <th key={s.id} ref={setRef(4 + i)} style={widthStyle(4 + i, "140px")} className="!text-center">
                      {s.name} · {dollars(s.price_cents)}<SortCaret field={`svc:${s.id}`} type="bool" sort={sort} toggle={onSort} /><Grip index={4 + i} />
                    </th>
                  ))}
                  <th ref={setRef(4 + (tab.services?.length || 0))} style={widthStyle(4 + (tab.services?.length || 0), "100px")} className="!text-center">Scheduled<SortCaret field="scheduled" type="bool" sort={sort} toggle={onSort} /><Grip index={4 + (tab.services?.length || 0)} /></th>
                  <th ref={setRef(5 + (tab.services?.length || 0))} style={widthStyle(5 + (tab.services?.length || 0), "80px")} className="!text-center">Paid<SortCaret field="paid" type="bool" sort={sort} toggle={onSort} /><Grip index={5 + (tab.services?.length || 0)} /></th>
                  <th ref={setRef(6 + (tab.services?.length || 0))} style={widthStyle(6 + (tab.services?.length || 0), "80px")} className="!text-center">Done<SortCaret field="done" type="bool" sort={sort} toggle={onSort} /><Grip index={6 + (tab.services?.length || 0)} /></th>
                  <th ref={setRef(7 + (tab.services?.length || 0))} style={widthStyle(7 + (tab.services?.length || 0), "130px")}>Status<SortCaret field="status" type="string" sort={sort} toggle={onSort} /><Grip index={7 + (tab.services?.length || 0)} /></th>
                  <th ref={setRef(8 + (tab.services?.length || 0))} style={widthStyle(8 + (tab.services?.length || 0), "100px")} className="!text-right">Total<SortCaret field="total" type="number" sort={sort} toggle={onSort} /><Grip index={8 + (tab.services?.length || 0)} /></th>
                  <th ref={setRef(9 + (tab.services?.length || 0))} style={widthStyle(9 + (tab.services?.length || 0), "200px")}>Notes<SortCaret field="notes" type="string" sort={sort} toggle={onSort} /><Grip index={9 + (tab.services?.length || 0)} /></th>
                  <th ref={setRef(10 + (tab.services?.length || 0))} style={widthStyle(10 + (tab.services?.length || 0), "48px")} />
                </tr>
              </thead>
              <tbody>
                {display.map((r) => (
                  <tr key={r.id} data-testid={`row-${r.id}`} {...(manualActive ? drag.rowProps(r.id) : {})}>
                    <td>
                      <div className="flex items-center gap-1">
                        {manualActive && (
                          <button {...drag.handleProps(r.id)} className="shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-foreground" title="Drag to reorder" data-testid={`row-drag-${r.id}`} aria-label="Drag to reorder">
                            <GripVertical size={13} />
                          </button>
                        )}
                        <input
                          defaultValue={r.client}
                          onBlur={(e) =>
                            e.target.value !== r.client && update(r.id, { client: e.target.value })
                          }
                          className="hud-input !border-transparent hover:!border-border !py-1"
                          placeholder="Client"
                        />
                      </div>
                    </td>
                    <td>
                      <input
                        defaultValue={r.subject}
                        onBlur={(e) =>
                          e.target.value !== r.subject && update(r.id, { subject: e.target.value })
                        }
                        className="hud-input !border-transparent hover:!border-border !py-1"
                        placeholder={tab.subject_label || "Subject"}
                      />
                    </td>
                    <td>
                      <input
                        type="date"
                        defaultValue={r.date || ""}
                        onBlur={(e) => update(r.id, { date: e.target.value || null })}
                        className="hud-input !py-1"
                      />
                    </td>
                    <td>
                      <input
                        type="time"
                        defaultValue={r.time || ""}
                        onBlur={(e) => update(r.id, { time: e.target.value || null })}
                        className="hud-input !py-1"
                      />
                    </td>
                    {(tab.services || []).map((s) => (
                      <td key={s.id} className="!text-center">
                        <Check
                          checked={!!(r.services || {})[s.id]}
                          onChange={(v) => toggleService(r, s.id, v)}
                          testid={`svc-${s.id}-${r.id}`}
                        />
                      </td>
                    ))}
                    <td className="!text-center">
                      <Check checked={!!r.scheduled} onChange={(v) => update(r.id, { scheduled: v })} />
                    </td>
                    <td className="!text-center">
                      <Check checked={!!r.paid} onChange={(v) => update(r.id, { paid: v })} />
                    </td>
                    <td className="!text-center">
                      <Check checked={!!r.done} onChange={(v) => update(r.id, { done: v })} />
                    </td>
                    <td>
                      <span className={statusChipCls(r.status)}>{r.status}</span>
                    </td>
                    <td className="num font-semibold text-hud">{dollars(r.total_cents)}</td>
                    <td>
                      <input
                        defaultValue={r.notes}
                        onBlur={(e) =>
                          e.target.value !== r.notes && update(r.id, { notes: e.target.value })
                        }
                        className="hud-input !border-transparent hover:!border-border !py-1"
                        placeholder="Notes"
                      />
                    </td>
                    <td>
                      <button
                        onClick={() => remove(r.id)}
                        className="text-muted-foreground hover:text-hud"
                        data-testid={`del-${r.id}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={20} className="text-center text-muted-foreground py-10">
                      Empty. Click <span className="text-hud">NEW ORDER</span> to begin.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <table className="ox-table" style={{ tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th ref={setRef(0)} style={widthStyle(0, "60px")} className="!text-center">Done<SortCaret field="list_done" type="bool" sort={sort} toggle={onSort} /><Grip index={0} /></th>
                  <th ref={setRef(1)} style={widthStyle(1, "300px")}>Title<SortCaret field="title" type="string" sort={sort} toggle={onSort} testId="sort-tab-title" /><Grip index={1} /></th>
                  <th ref={setRef(2)} style={widthStyle(2, "130px")}>Date<SortCaret field="date" type="date" sort={sort} toggle={onSort} /><Grip index={2} /></th>
                  <th ref={setRef(3)} style={widthStyle(3, "120px")} className="!text-right">Amount<SortCaret field="amount" type="number" sort={sort} toggle={onSort} /><Grip index={3} /></th>
                  <th ref={setRef(4)} style={widthStyle(4, "240px")}>Notes<SortCaret field="notes" type="string" sort={sort} toggle={onSort} /><Grip index={4} /></th>
                  <th ref={setRef(5)} style={widthStyle(5, "48px")} />
                </tr>
              </thead>
              <tbody>
                {display.map((r) => (
                  <tr key={r.id} data-testid={`row-${r.id}`} {...(manualActive ? drag.rowProps(r.id) : {})}>
                    <td className="!text-center">
                      <div className="flex items-center justify-center gap-1">
                        {manualActive && (
                          <button {...drag.handleProps(r.id)} className="shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-foreground" title="Drag to reorder" data-testid={`row-drag-${r.id}`} aria-label="Drag to reorder">
                            <GripVertical size={13} />
                          </button>
                        )}
                        <Check
                          checked={!!r.list_done}
                          onChange={(v) => update(r.id, { list_done: v })}
                        />
                      </div>
                    </td>
                    <td>
                      <input
                        defaultValue={r.title}
                        onBlur={(e) =>
                          e.target.value !== r.title && update(r.id, { title: e.target.value })
                        }
                        className={`hud-input !border-transparent hover:!border-border !py-1 ${r.list_done ? "line-through opacity-60" : ""}`}
                        placeholder="Item"
                      />
                    </td>
                    <td>
                      <input
                        type="date"
                        defaultValue={r.date || ""}
                        onBlur={(e) => update(r.id, { date: e.target.value || null })}
                        className="hud-input !py-1"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        defaultValue={(r.amount_cents || 0) / 100}
                        onBlur={(e) =>
                          update(r.id, { amount_cents: Math.round(Number(e.target.value || 0) * 100) })
                        }
                        className="hud-input !py-1 !text-right num"
                        placeholder="0"
                      />
                    </td>
                    <td>
                      <input
                        defaultValue={r.notes}
                        onBlur={(e) =>
                          e.target.value !== r.notes && update(r.id, { notes: e.target.value })
                        }
                        className="hud-input !border-transparent hover:!border-border !py-1"
                        placeholder="Notes"
                      />
                    </td>
                    <td>
                      <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-hud">
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-muted-foreground py-10">
                      Empty. Click <span className="text-hud">NEW ITEM</span> to begin.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {configOpen && (
        <TabConfigModal tab={tab} onClose={() => setConfigOpen(false)} onSaved={load} />
      )}

      {importOpen && (
        <ImportModal
          target="tab"
          tab={tab}
          onClose={() => setImportOpen(false)}
          onImported={async () => { await load(); setImportBump((b) => b + 1); }}
        />
      )}
    </div>
  );
}


function TabConfigModal({ tab, onClose, onSaved }) {
  const [label, setLabel] = useState(tab.label);
  const [subjectLabel, setSubjectLabel] = useState(tab.subject_label || "Subject");
  const [services, setServices] = useState(tab.services || []);
  const [busy, setBusy] = useState(false);

  const isTracker = tab.type === "tracker";

  const addSvc = () =>
    setServices((p) => [...p, { id: `new-${Date.now()}-${p.length}`, name: "", price_cents: 0, order: p.length }]);
  const removeSvc = (id) => setServices((p) => p.filter((s) => s.id !== id));
  const updSvc = (id, patch) =>
    setServices((p) => p.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        label: label.trim() || tab.label,
        subject_label: subjectLabel.trim() || "Subject",
        services: services.filter((s) => s.name.trim()),
      };
      await api.put(`/tabs/${tab.id}`, payload);
      await onSaved();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (tab.system) return alert("System tab cannot be deleted.");
    if (!window.confirm(`Delete "${tab.label}" and all its rows?`)) return;
    await api.delete(`/tabs/${tab.id}`);
    await onSaved();
    onClose();
    window.location.href = "/";
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/65 backdrop-blur-sm flex items-start justify-center pt-12 px-4 animate-fadein"
      onClick={onClose}
      data-testid="tab-config"
    >
      <div
        className="hud-frame w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ background: "hsl(var(--popover))" }}
      >
        <div className="tread-plate h-1.5" />
        <div className="p-6 space-y-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="kpi-label">Configure Tab</div>
              <h2 className="display text-2xl font-bold uppercase mt-1">{tab.label}</h2>
            </div>
            <button onClick={onClose} className="hud-btn !px-2 !py-1" data-testid="tc-close">
              <X size={11} />
            </button>
          </div>

          <div>
            <div className="kpi-label">Tab Name</div>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="hud-input mt-2"
              data-testid="tc-label"
            />
          </div>

          {isTracker && (
            <>
              <div>
                <div className="kpi-label">Subject Column Label</div>
                <input
                  value={subjectLabel}
                  onChange={(e) => setSubjectLabel(e.target.value)}
                  className="hud-input mt-2"
                  placeholder="Property / Project / Client Site"
                  data-testid="tc-subject"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="kpi-label">Services & Line Items</div>
                  <button onClick={addSvc} className="hud-btn !px-2 !py-1 flex items-center gap-1" data-testid="tc-add-svc">
                    <Plus size={11} /> ADD
                  </button>
                </div>
                <div className="space-y-2">
                  {services.map((s) => (
                    <div key={s.id} className="flex items-center gap-2" data-testid={`tc-svc-${s.id}`}>
                      <GripVertical size={12} className="text-muted-foreground" />
                      <input
                        value={s.name}
                        onChange={(e) => updSvc(s.id, { name: e.target.value })}
                        className="hud-input !py-1.5 flex-1"
                        placeholder="Service name"
                      />
                      <div className="flex items-center gap-1">
                        <span className="mono text-xs text-muted-foreground">$</span>
                        <input
                          type="number"
                          value={(s.price_cents || 0) / 100}
                          onChange={(e) =>
                            updSvc(s.id, { price_cents: Math.round(Number(e.target.value || 0) * 100) })
                          }
                          className="hud-input !py-1.5 w-24 !text-right num"
                          step="1"
                          min="0"
                        />
                      </div>
                      <button
                        onClick={() => removeSvc(s.id)}
                        className="text-muted-foreground hover:text-hud"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                  {services.length === 0 && (
                    <div className="text-sm text-muted-foreground">
                      No services yet — add line items above (each charges its price when checked on an order).
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          <div className="flex items-center justify-between pt-4 border-t border-border">
            {!tab.system ? (
              <button onClick={del} className="hud-btn !text-[hsl(var(--oxblood))]" data-testid="tc-delete">
                <Trash2 size={11} /> DELETE TAB
              </button>
            ) : (
              <span className="mono text-[10px] text-muted-foreground tracking-widest uppercase">
                System tab · cannot be deleted
              </span>
            )}
            <div className="flex items-center gap-2">
              <button onClick={onClose} className="hud-btn" data-testid="tc-cancel">
                CANCEL
              </button>
              <button
                onClick={save}
                disabled={busy}
                className="hud-btn hud-btn-primary"
                data-testid="tc-save"
              >
                {busy ? "SAVING…" : "SAVE"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
